import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n";
import { Badge } from "@/components/ui/badge";
import { Trophy, CaretDown, CaretRight, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const nameOf = (map, id) => (map[id] ? `${map[id].last_name} ${map[id].first_name}` : null);

export default function ResultsTab({ eventId, event }) {
  const t = useT();
  const [pools, setPools] = useState([]);
  const [matches, setMatches] = useState([]);
  const [byId, setById] = useState({});
  const [open, setOpen] = useState({});
  const [discipline, setDiscipline] = useState("combat");
  const [swapSel, setSwapSel] = useState(null);
  const clubCfg = event?.club_standings || { method: "medals", gold_points: 10, silver_points: 5, bronze_points: 3 };

  const load = useCallback(async () => {
    const [p, m, a] = await Promise.all([
      api.get(`/events/${eventId}/pools`),
      api.get(`/events/${eventId}/matches`),
      api.get(`/events/${eventId}/athletes`),
    ]);
    setPools(p.data);
    setMatches(m.data);
    setById(Object.fromEntries(a.data.map((x) => [x.id, x])));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  const poolsWithMatches = pools.filter((p) => matches.some((m) => m.pool_id === p.id));
  const baseDisc = ["combat", "poomsae"].filter((d) => poolsWithMatches.some((p) => p.discipline === d));
  const availableDisc = poolsWithMatches.length ? [...baseDisc, "clubs"] : baseDisc;
  useEffect(() => {
    if (availableDisc.length && !availableDisc.includes(discipline)) setDiscipline(availableDisc[0]);
    /* eslint-disable-next-line */
  }, [poolsWithMatches.length]);
  const displayedPools = poolsWithMatches.filter((p) => p.discipline === discipline);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  const onSlotClick = async (poolId, athleteId) => {
    if (!athleteId) return;
    if (!swapSel || swapSel.poolId !== poolId) { setSwapSel({ poolId, athleteId }); return; }
    if (swapSel.athleteId === athleteId) { setSwapSel(null); return; }
    try {
      await api.post(`/pools/${poolId}/swap`, { a_id: swapSel.athleteId, b_id: athleteId });
      setSwapSel(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSwapSel(null); }
  };

  const doTiebreak = async (poolId, ids) => {
    try {
      await api.post(`/pools/${poolId}/tiebreak`, { athlete_ids: ids });
      toast.success(t("res.tiebreakCreated"));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportResults = () => {
    const esc = (v) => String(v ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const nm = (id, empty) => (byId[id] ? esc(`${byId[id].last_name} ${byId[id].first_name}`) : (empty || t("res.tbd")));
    const standings = (pool, pm) => {
      const isScore = pool.bracket_type === "score";
      if (isScore) {
        return pm.map((m) => ({ id: m.athlete_id || m.red_athlete_id, value: m.state?.poomsae_average ?? (m.state?.red?.score ?? null) }))
          .sort((a, b) => (b.value ?? -1) - (a.value ?? -1));
      }
      const wins = {};
      pm.forEach((m) => {
        [m.red_athlete_id, m.blue_athlete_id].forEach((id) => { if (id && !(id in wins)) wins[id] = 0; });
        if (m.state?.winner) { const w = m.state.winner === "red" ? m.red_athlete_id : m.blue_athlete_id; if (w) wins[w] = (wins[w] || 0) + 1; }
      });
      return Object.entries(wins).map(([id, v]) => ({ id, value: v })).sort((a, b) => b.value - a.value);
    };
    const elimHtml = (pm) => {
      const rounds = {};
      pm.forEach((m) => { const r = m.round || 1; (rounds[r] = rounds[r] || []).push(m); });
      const rk = Object.keys(rounds).sort((a, b) => a - b);
      const final = rk.length ? rounds[rk[rk.length - 1]][0] : null;
      const champ = final?.state?.winner ? (final.state.winner === "red" ? final.red_athlete_id : final.blue_athlete_id) : null;
      let html = champ ? `<p class="champ">${t("res.champion")}: <b>${nm(champ)}</b></p>` : "";
      html += rk.map((r) => `<div class="round"><h4>${t("res.round")} ${r}</h4>` + rounds[r].filter((m) => m.red_athlete_id || m.blue_athlete_id).map((m) => {
        const w = m.state?.winner;
        const empty = r === 1 ? t("res.bye") : t("res.tbd");
        return `<div class="mrow"><span class="${w === "red" ? "win" : ""}">${nm(m.red_athlete_id, empty)}</span> — <span class="${w === "blue" ? "win" : ""}">${nm(m.blue_athlete_id, empty)}</span></div>`;
      }).join("") + `</div>`).join("");
      return html;
    };
    const orderedPools = [...poolsWithMatches].sort((a, b) => {
      const rank = (d) => (d === "combat" ? 0 : 1);
      return rank(a.discipline) - rank(b.discipline);
    });
    let lastDisc = null;
    const sections = orderedPools.map((pool) => {
      const heading = pool.discipline !== lastDisc
        ? `<h2 class="disc ${pool.discipline}">${pool.discipline === "combat" ? t("type.combat") : t("type.poomsae")}</h2>`
        : "";
      lastDisc = pool.discipline;
      const pm = matches.filter((m) => m.pool_id === pool.id);
      const isScore = pool.bracket_type === "score";
      let body;
      if (pool.bracket_type === "single_elim") body = elimHtml(pm);
      else {
        const rows = standings(pool, pm);
        body = `<table class="tbl"><thead><tr><th>#</th><th>${t("res.standings")}</th><th>${isScore ? t("res.avg") : t("res.wins")}</th></tr></thead><tbody>` +
          rows.map((r, i) => `<tr><td>${i + 1}</td><td>${nm(r.id)}</td><td>${r.value == null ? "—" : r.value}</td></tr>`).join("") + `</tbody></table>`;
      }
      return `${heading}<section><h3>${esc(pool.name)} <small>(${t("bracket." + pool.bracket_type)})</small></h3>${body}</section>`;
    }).join("");
    const clubRows = computeClubRows(poolsWithMatches, matches, byId, clubCfg, t("clubres.noClub"));
    const isClubPoints = clubCfg.method === "points";
    const clubSection = clubRows.length
      ? `<h2 class="disc clubs">${t("res.clubs")}</h2><section><table class="tbl"><thead><tr>`
        + `<th>${t("clubres.rank")}</th><th>${t("clubres.club")}</th><th>${t("clubres.athletes")}</th>`
        + `<th>${t("clubres.gold")}</th><th>${t("clubres.silver")}</th><th>${t("clubres.bronze")}</th><th>${t("clubres.points")}</th></tr></thead><tbody>`
        + clubRows.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.club)}</td><td>${r.athletes}</td><td>${r.g}</td><td>${r.s}</td><td>${r.b}</td><td>${isClubPoints ? r.points : "—"}</td></tr>`).join("")
        + `</tbody></table></section>`
      : "";
    const w = window.open("", "_blank");
    if (!w) return;
    const html = (`<!doctype html><html><head><meta charset="utf-8"><title>${t("res.exportTitle")}</title><style>
      @page{size:A4;margin:16mm;} body{font-family:Arial,sans-serif;color:#111;}
      h1{font-size:28px;margin:0 0 4px;} .date{color:#666;margin-bottom:20px;font-size:12px;}
      section{margin-bottom:22px;page-break-inside:avoid;} h3{font-size:18px;border-bottom:2px solid #111;padding-bottom:4px;} h3 small{font-weight:normal;color:#666;font-size:12px;}
      h2.disc{font-size:22px;margin:26px 0 12px;padding:6px 12px;color:#fff;border-radius:4px;text-transform:uppercase;letter-spacing:1px;} h2.disc:first-of-type{margin-top:0;} h2.disc.combat{background:#E62E2D;} h2.disc.poomsae{background:#1A56C8;} h2.disc.clubs{background:#e8b800;color:#111;}
      .tbl{width:100%;border-collapse:collapse;margin-top:8px;} .tbl th,.tbl td{border:1px solid #ccc;padding:6px 10px;text-align:left;font-size:13px;} .tbl th{background:#f0f0f0;}
      .champ{font-size:16px;margin:8px 0;} .round{margin:8px 0;} .round h4{margin:6px 0;font-size:12px;color:#555;text-transform:uppercase;letter-spacing:1px;}
      .mrow{font-size:13px;padding:2px 0;} .win{font-weight:bold;}
    </style></head><body>
      <h1>${t("res.exportTitle")}</h1><div class="date">${new Date().toLocaleString()}</div>
      ${sections}
      ${clubSection}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`);
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    w.location.href = URL.createObjectURL(blob);
  };

  if (poolsWithMatches.length === 0)
    return <div className="border border-dashed border-border rounded-lg p-16 text-center text-muted-foreground" data-testid="results-empty">{t("res.noData")}</div>;

  return (
    <div className="space-y-4" data-testid="results-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{t("res.title")}</h2>
          {availableDisc.length > 1 && (
            <div className="flex rounded-md border border-border overflow-hidden">
              {availableDisc.map((d) => (
                <button key={d} onClick={() => setDiscipline(d)} data-testid={`result-disc-tab-${d}`}
                  className={`px-3 py-1.5 text-sm font-medium ${discipline === d ? (d === "combat" ? "bg-hong text-white" : d === "poomsae" ? "bg-chung text-white" : "bg-gold text-black") : "text-muted-foreground"}`}>
                  {d === "combat" ? t("type.combat") : d === "poomsae" ? t("type.poomsae") : t("res.clubs")}
                </button>
              ))}
            </div>
          )}
        </div>
        <Button onClick={exportResults} className="gap-2" data-testid="export-results-btn"><DownloadSimple /> {t("res.export")}</Button>
      </div>
      {discipline === "clubs" && <ClubStandings pools={poolsWithMatches} matches={matches} byId={byId} cfg={clubCfg} t={t} />}
      {displayedPools.map((pool) => {
        const pm = matches.filter((m) => m.pool_id === pool.id);
        const isOpen = open[pool.id] ?? true;
        const locked = pm.some((m) => m.token_consumed);
        const isElim = pool.bracket_type === "single_elim";
        return (
          <div key={pool.id} className="border border-border rounded-lg bg-card overflow-hidden" data-testid={`result-pool-${pool.id}`}>
            <div className="w-full flex items-center justify-between p-4 hover:bg-secondary/40">
              <button onClick={() => toggle(pool.id)} className="flex items-center gap-3 flex-1 text-left" data-testid={`result-pool-toggle-${pool.id}`}>
                {isOpen ? <CaretDown size={16} /> : <CaretRight size={16} />}
                <span className="font-bold text-lg">{pool.name}</span>
                <Badge variant="secondary" className="text-[10px] uppercase">{t(`bracket.${pool.bracket_type}`)}</Badge>
              </button>
            </div>
            {isOpen && (
              <div className="p-4 pt-0">
                {isElim ? (
                  <>
                    <Bracket matches={pm} byId={byId} t={t} locked={locked} swapSel={swapSel} onSlotClick={(aid) => onSlotClick(pool.id, aid)} />
                    {!locked && <p className="text-xs text-muted-foreground mt-3" data-testid="swap-hint">{t("res.swapHint")}</p>}
                  </>
                ) : (
                  <Ranking pool={pool} matches={pm} byId={byId} t={t} onTiebreak={doTiebreak} />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Bracket({ matches, byId, t, locked, swapSel, onSlotClick }) {
  const rounds = useMemo(() => {
    const g = {};
    matches.forEach((m) => { const r = m.round || 1; (g[r] = g[r] || []).push(m); });
    return Object.keys(g).sort((a, b) => a - b).map((k) => g[k]);
  }, [matches]);

  const finalMatch = rounds.length ? rounds[rounds.length - 1][0] : null;
  const champ = finalMatch?.state?.winner
    ? (finalMatch.state.winner === "red" ? finalMatch.red_athlete_id : finalMatch.blue_athlete_id)
    : null;

  return (
    <div>
      {champ && (
        <div className="flex items-center gap-2 mb-4 text-gold font-bold" data-testid="bracket-champion">
          <Trophy weight="fill" /> {t("res.champion")}: {nameOf(byId, champ)}
        </div>
      )}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {rounds.map((rm, ri) => (
          <div key={rm[0]?.round ?? ri} className="min-w-[220px] space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("res.round")} {ri + 1}</div>
            {rm.filter((m) => m.red_athlete_id || m.blue_athlete_id).map((m) => (
              <div key={m.id} className="border border-border rounded-md overflow-hidden text-sm" data-testid={`bracket-match-${m.id}`}>
                <Slot name={nameOf(byId, m.red_athlete_id)} win={m.state?.winner === "red"} color="red" t={t} emptyLabel={t(ri === 0 ? "res.bye" : "res.tbd")}
                  athleteId={m.red_athlete_id} clickable={ri === 0 && !locked} selected={swapSel && swapSel.athleteId === m.red_athlete_id} onClick={onSlotClick} />
                <div className="border-t border-border" />
                <Slot name={nameOf(byId, m.blue_athlete_id)} win={m.state?.winner === "blue"} color="blue" t={t} emptyLabel={t(ri === 0 ? "res.bye" : "res.tbd")}
                  athleteId={m.blue_athlete_id} clickable={ri === 0 && !locked} selected={swapSel && swapSel.athleteId === m.blue_athlete_id} onClick={onSlotClick} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Slot({ name, win, color, t, athleteId, clickable, selected, onClick, emptyLabel }) {
  const active = clickable && athleteId;
  return (
    <div className={`flex items-center justify-between px-3 py-2 ${win ? "bg-gold/15" : ""} ${selected ? "ring-2 ring-gold" : ""} ${active ? "cursor-pointer hover:bg-secondary/60" : ""}`}
      onClick={() => active && onClick && onClick(athleteId)}
      data-testid={athleteId ? `bracket-slot-${athleteId}` : undefined}>
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-4 rounded-sm ${color === "red" ? "bg-hong" : "bg-chung"}`} />
        <span className={win ? "font-bold text-white" : "text-muted-foreground"}>{name || emptyLabel || t("res.tbd")}</span>
      </div>
      {win && <Trophy weight="fill" size={14} className="text-gold" />}
    </div>
  );
}

function Ranking({ pool, matches, byId, t, onTiebreak }) {
  const isScore = pool.bracket_type === "score";
  const scoreMatches = matches.filter((m) => !m.tiebreak);
  const tbMatches = matches.filter((m) => m.tiebreak);

  const tbWins = {};
  tbMatches.forEach((m) => {
    if (m.state?.winner) {
      const w = m.state.winner === "red" ? m.red_athlete_id : m.blue_athlete_id;
      if (w) tbWins[w] = (tbWins[w] || 0) + 1;
    }
  });

  const rows = useMemo(() => {
    if (isScore) {
      return scoreMatches
        .map((m) => ({ id: m.athlete_id || m.red_athlete_id, value: m.state?.poomsae_average ?? null }))
        .sort((a, b) => {
          const dv = (b.value ?? -1) - (a.value ?? -1);
          if (dv !== 0) return dv;
          return (tbWins[b.id] || 0) - (tbWins[a.id] || 0);
        });
    }
    const wins = {};
    scoreMatches.forEach((m) => {
      [m.red_athlete_id, m.blue_athlete_id].forEach((id) => { if (id && !(id in wins)) wins[id] = 0; });
      if (m.state?.winner) {
        const w = m.state.winner === "red" ? m.red_athlete_id : m.blue_athlete_id;
        if (w) wins[w] = (wins[w] || 0) + 1;
      }
    });
    return Object.entries(wins).map(([id, v]) => ({ id, value: v })).sort((a, b) => b.value - a.value);
  }, [pool, matches]); // eslint-disable-line react-hooks/exhaustive-deps

  const tieGroups = [];
  if (isScore) {
    const byVal = {};
    rows.forEach((r) => { if (r.value != null) (byVal[r.value] = byVal[r.value] || []).push(r.id); });
    Object.entries(byVal).forEach(([value, ids]) => { if (ids.length >= 2) tieGroups.push({ value, ids }); });
  }
  const groupTb = (ids) => tbMatches.filter((m) => ids.includes(m.red_athlete_id) && ids.includes(m.blue_athlete_id));

  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t("res.standings")}</div>
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={r.id} className={`flex items-center justify-between px-3 py-2 rounded-md ${i === 0 ? "bg-gold/10" : "bg-secondary/40"}`} data-testid={`ranking-row-${r.id}`}>
            <div className="flex items-center gap-3">
              <span className={`font-score text-lg w-6 ${i === 0 ? "text-gold" : "text-muted-foreground"}`}>{i + 1}</span>
              <span className={i === 0 ? "font-bold" : ""}>{nameOf(byId, r.id)}</span>
              {i === 0 && <Trophy weight="fill" size={14} className="text-gold" />}
              {isScore && tbWins[r.id] > 0 && <span className="text-[9px] font-bold uppercase text-gold border border-gold rounded px-1" title={t("res.tiebreak")}>SP</span>}
            </div>
            <span className="font-monot tabular text-sm">
              {r.value == null ? t("res.pending") : isScore ? `${r.value} ${t("res.avg").toLowerCase()}` : `${r.value} ${t("res.wins").toLowerCase()}`}
            </span>
          </div>
        ))}
      </div>

      {isScore && tieGroups.length > 0 && (
        <div className="mt-4 border-t border-border pt-3" data-testid="tiebreak-panel">
          <div className="text-xs uppercase tracking-widest text-warn mb-2">{t("res.tiebreak")}</div>
          {tieGroups.map((g, gi) => {
            const tb = groupTb(g.ids);
            const pending = tb.length > 0 && tb.some((m) => m.status !== "finished");
            return (
              <div key={g.ids.join("-")} className="flex items-center justify-between gap-3 bg-secondary/40 rounded-md px-3 py-2 mb-1" data-testid={`tiebreak-group-${gi}`}>
                <div className="text-sm">
                  <span className="text-muted-foreground">{g.value} {t("res.avg").toLowerCase()} — </span>
                  {g.ids.map((id) => nameOf(byId, id)).join(", ")}
                  {pending && <span className="ml-2 text-warn text-xs">({t("res.tiebreakPending")})</span>}
                </div>
                <Button variant="outline" size="sm" className="h-8" onClick={() => onTiebreak(pool.id, g.ids)} data-testid={`tiebreak-btn-${gi}`}>
                  {tb.length ? t("res.tiebreakRegen") : t("res.tiebreakBtn")}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function computeClubRows(pools, matches, byId, cfg, noClubLabel) {
  const clubOf = (id) => (byId[id]?.school || "").trim() || noClubLabel;
  const competing = {};
  const medals = {};
  const bump = (club, k) => { medals[club] = medals[club] || { g: 0, s: 0, b: 0 }; medals[club][k]++; };
  pools.forEach((pool) => {
    const pm = matches.filter((m) => m.pool_id === pool.id);
    pm.forEach((m) => {
      [m.athlete_id, m.red_athlete_id, m.blue_athlete_id].forEach((id) => {
        if (id && byId[id]) { const c = clubOf(id); (competing[c] = competing[c] || new Set()).add(id); }
      });
    });
    const md = poolMedals(pool, pm, !!cfg.double_bronze);
    md.gold.forEach((id) => byId[id] && bump(clubOf(id), "g"));
    md.silver.forEach((id) => byId[id] && bump(clubOf(id), "s"));
    md.bronze.forEach((id) => byId[id] && bump(clubOf(id), "b"));
  });
  const clubs = new Set([...Object.keys(competing), ...Object.keys(medals)]);
  const gp = Number(cfg.gold_points) || 0, sp = Number(cfg.silver_points) || 0, bp = Number(cfg.bronze_points) || 0;
  const isPoints = cfg.method === "points";
  const out = [...clubs].map((club) => {
    const m = medals[club] || { g: 0, s: 0, b: 0 };
    return { club, athletes: competing[club]?.size || 0, g: m.g, s: m.s, b: m.b, points: m.g * gp + m.s * sp + m.b * bp };
  });
  out.sort((a, b) => isPoints
    ? (b.points - a.points || b.g - a.g || b.s - a.s || a.club.localeCompare(b.club))
    : (b.g - a.g || b.s - a.s || b.b - a.b || b.athletes - a.athletes || a.club.localeCompare(b.club)));
  return out;
}

function poolMedals(pool, pm, doubleBronze) {
  let ordered = [];
  if (pool.bracket_type === "score") {
    ordered = pm.filter((m) => !m.tiebreak)
      .map((m) => ({ id: m.athlete_id || m.red_athlete_id, value: m.state?.poomsae_average ?? null }))
      .filter((r) => r.id && r.value != null)
      .sort((a, b) => b.value - a.value)
      .map((r) => r.id);
  } else if (pool.bracket_type === "single_elim") {
    const rounds = {};
    pm.forEach((m) => { const r = m.round || 1; (rounds[r] = rounds[r] || []).push(m); });
    const rk = Object.keys(rounds).map(Number).sort((a, b) => a - b);
    if (rk.length) {
      const finalM = rounds[rk[rk.length - 1]][0];
      if (finalM?.state?.winner) {
        const w = finalM.state.winner === "red" ? finalM.red_athlete_id : finalM.blue_athlete_id;
        const l = finalM.state.winner === "red" ? finalM.blue_athlete_id : finalM.red_athlete_id;
        if (w) ordered.push(w);
        if (l) ordered.push(l);
      }
      if (rk.length >= 2) {
        rounds[rk[rk.length - 2]].forEach((m) => {
          if (m.state?.winner) {
            const l = m.state.winner === "red" ? m.blue_athlete_id : m.red_athlete_id;
            if (l) ordered.push(l);
          }
        });
      }
    }
  } else {
    const wins = {};
    pm.filter((m) => !m.tiebreak).forEach((m) => {
      [m.red_athlete_id, m.blue_athlete_id].forEach((id) => { if (id && !(id in wins)) wins[id] = 0; });
      if (m.state?.winner) { const w = m.state.winner === "red" ? m.red_athlete_id : m.blue_athlete_id; if (w) wins[w] = (wins[w] || 0) + 1; }
    });
    if (pm.some((m) => m.state?.winner)) {
      ordered = Object.entries(wins).map(([id, v]) => ({ id, value: v })).sort((a, b) => b.value - a.value).map((r) => r.id);
    }
  }
  const gold = ordered[0] ? [ordered[0]] : [];
  const silver = ordered[1] ? [ordered[1]] : [];
  const bronze = [];
  if (ordered[2]) bronze.push(ordered[2]);
  if (doubleBronze && ordered[3]) bronze.push(ordered[3]);
  return { gold, silver, bronze };
}

function ClubStandings({ pools, matches, byId, cfg, t }) {
  const isPoints = cfg.method === "points";
  const rows = useMemo(() => computeClubRows(pools, matches, byId, cfg, t("clubres.noClub")),
    [pools, matches, byId, cfg, t]);

  if (rows.length === 0)
    return <div className="border border-dashed border-border rounded-lg p-12 text-center text-muted-foreground" data-testid="club-standings-empty">{t("res.noData")}</div>;

  return (
    <div className="border border-border rounded-lg bg-card overflow-x-auto" data-testid="club-standings-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-muted-foreground uppercase text-[11px] tracking-widest">
            <th className="px-4 py-3 w-12">{t("clubres.rank")}</th>
            <th className="px-4 py-3">{t("clubres.club")}</th>
            <th className="px-4 py-3 text-center">{t("clubres.athletes")}</th>
            <th className="px-4 py-3 text-center">{t("clubres.gold")}</th>
            <th className="px-4 py-3 text-center">{t("clubres.silver")}</th>
            <th className="px-4 py-3 text-center">{t("clubres.bronze")}</th>
            <th className="px-4 py-3 text-center">{t("clubres.points")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.club} className={`border-b border-border/60 ${i === 0 ? "bg-gold/10" : ""}`} data-testid={`club-row-${i}`}>
              <td className="px-4 py-3 font-score text-lg">
                <span className={i === 0 ? "text-gold" : "text-muted-foreground"}>{i + 1}</span>
              </td>
              <td className="px-4 py-3 font-semibold flex items-center gap-2">
                {i === 0 && <Trophy weight="fill" size={15} className="text-gold" />}{r.club}
              </td>
              <td className="px-4 py-3 text-center tabular">{r.athletes}</td>
              <td className="px-4 py-3 text-center tabular font-semibold text-gold">{r.g}</td>
              <td className="px-4 py-3 text-center tabular text-muted-foreground">{r.s}</td>
              <td className="px-4 py-3 text-center tabular text-amber-700">{r.b}</td>
              <td className="px-4 py-3 text-center tabular font-bold">{isPoints ? r.points : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
